import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    MenuItem,
    Button,
    Alert,
    CircularProgress,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Grid
} from '@mui/material';
import {
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import api from '../../utils/api';

const InventoryTransactionHistory = ({ branchId }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [transactions, setTransactions] = useState([]);
    const [products, setProducts] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [filters, setFilters] = useState({
        productId: '',
        startDate: null,
        endDate: null,
        transactionType: ''
    });

    const TRANSACTION_TYPES = {
        stock_in: { label: 'Stock In', color: 'success' },
        stock_out: { label: 'Stock Out', color: 'error' },
        adjustment: { label: 'Adjustment', color: 'warning' },
        transfer: { label: 'Transfer', color: 'info' }
    };

    const fetchData = useCallback(async () => {
        try {
            const [productsRes] = await Promise.all([
                api.get('/products')
            ]);
            setProducts(productsRes.data.products || []);
        } catch (error) {
            console.error('Error fetching data:', error);
            setError('Failed to load reference data');
        }
    }, []);

    const fetchTransactions = useCallback(async () => {
        try {
            setLoading(true);
            const params = {
                limit: rowsPerPage,
                offset: page * rowsPerPage
            };

            if (filters.productId) params.productId = filters.productId;
            if (filters.startDate) params.startDate = filters.startDate.toISOString();
            if (filters.endDate) params.endDate = filters.endDate.toISOString();
            if (filters.transactionType) params.transactionType = filters.transactionType;

            const response = await api.get(`/branches/${branchId}/inventory/transactions`, {
                params
            });

            setTransactions(response.data.transactions || []);
            setError('');
        } catch (error) {
            console.error('Error fetching transactions:', error);
            setError('Failed to load transaction history');
        } finally {
            setLoading(false);
        }
    }, [branchId, filters, page, rowsPerPage]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        fetchTransactions();
    }, [fetchTransactions]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
        setPage(0); // Reset to first page when filtering
    };

    const handleRefresh = () => {
        fetchTransactions();
    };

    const formatDateTime = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    const getTransactionTypeChip = (type) => {
        const config = TRANSACTION_TYPES[type] || { label: type, color: 'default' };
        return (
            <Chip
                label={config.label}
                color={config.color}
                size="small"
                variant="outlined"
            />
        );
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Paper sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h6">
                            Inventory Transaction History
                        </Typography>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={handleRefresh}
                            disabled={loading}
                        >
                            Refresh
                        </Button>
                    </Box>

                    {/* Filters */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid size={3}>
                            <TextField
                                fullWidth
                                select
                                label="Product"
                                value={filters.productId}
                                onChange={(e) => handleFilterChange('productId', e.target.value)}
                            >
                                <MenuItem value="">All Products</MenuItem>
                                {products.map((product) => (
                                    <MenuItem key={product.id} value={product.id}>
                                        {product.name} ({product.sku})
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid size={3}>
                            <TextField
                                fullWidth
                                select
                                label="Transaction Type"
                                value={filters.transactionType}
                                onChange={(e) => handleFilterChange('transactionType', e.target.value)}
                            >
                                <MenuItem value="">All Types</MenuItem>
                                {Object.entries(TRANSACTION_TYPES).map(([key, config]) => (
                                    <MenuItem key={key} value={key}>
                                        {config.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid size={3}>
                            <DatePicker
                                label="Start Date"
                                value={filters.startDate}
                                onChange={(date) => handleFilterChange('startDate', date)}
                                renderInput={(params) => <TextField {...params} fullWidth />}
                            />
                        </Grid>
                        <Grid size={3}>
                            <DatePicker
                                label="End Date"
                                value={filters.endDate}
                                onChange={(date) => handleFilterChange('endDate', date)}
                                renderInput={(params) => <TextField {...params} fullWidth />}
                            />
                        </Grid>
                    </Grid>

                    {/* Transactions Table */}
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date & Time</TableCell>
                                    <TableCell>Product</TableCell>
                                    <TableCell>Transaction Type</TableCell>
                                    <TableCell align="right">Quantity</TableCell>
                                    <TableCell>Reference</TableCell>
                                    <TableCell>Created By</TableCell>
                                    <TableCell>Notes</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            <CircularProgress />
                                        </TableCell>
                                    </TableRow>
                                ) : transactions.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            No transactions found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    transactions.map((transaction) => (
                                        <TableRow key={transaction.id}>
                                            <TableCell>
                                                {formatDateTime(transaction.created_at)}
                                            </TableCell>
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {transaction.product_name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        SKU: {transaction.sku}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                {getTransactionTypeChip(transaction.transaction_type)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'medium' }}>
                                                {transaction.quantity}
                                            </TableCell>
                                            <TableCell>
                                                {transaction.reference_number || '-'}
                                            </TableCell>
                                            <TableCell>
                                                {transaction.created_by_name || 'System'}
                                            </TableCell>
                                            <TableCell>
                                                {transaction.notes || '-'}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        component="div"
                        count={-1} // Since we don't have total count, show "more" functionality
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(event, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(event) => {
                            setRowsPerPage(parseInt(event.target.value, 10));
                            setPage(0);
                        }}
                    />
                </Paper>
            </Box>
        </LocalizationProvider>
    );
};

export default InventoryTransactionHistory; 